// Pure persistence format helpers: the wire shape of a saved workbook and the
// app-id quirks. Kept free of the SDK so it is unit-testable in plain node —
// importing @gi-nx/iframe-sdk pulls in mini-iframe-rpc, which touches `window`
// at module load.

import type { Cell, PersistedWorkbook, Sheet, Workbook } from '@/model/types';
import { SCHEMA_VERSION } from '@/model/types';

export const ATTACHMENT_NAME = 'gtables-workbook.json';
/** `updateProjectApp` silently rejects past ~20,000 chars. Leave headroom. */
export const PUBLIC_CAP = 18_000;

export type SaveTarget = 'projectApp' | 'attachment' | 'local';
export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'retrying' | 'error';

export interface SaveStatus {
  state: SaveState;
  target: SaveTarget;
  /** Human-readable detail — shown verbatim, so it must say something true. */
  detail: string;
  lastSavedAt: number | null;
  failures: number;
}

// ---------------------------------------------------------------------------
// App id resolution
// ---------------------------------------------------------------------------

/**
 * `selectedProjectApp.id` is composite — `<projectId>-<appId>`, e.g. "42-167" —
 * and `updateProjectApp` REJECTS it with "app id mismatch". It wants the bare
 * app id. (Verified across paper, spaces, instaplan, numbers.)
 */
export function bareAppId(raw: string): string {
  const m = /^\d+-(.+)$/.exec(raw);
  return m && m[1] ? m[1] : raw;
}

/** Giraffe's shape varies across builds, so try every id-bearing field. */
export function resolveAppId(selectedApp: unknown, appsByAppID: unknown): string | null {
  const app = (selectedApp ?? null) as Record<string, unknown> | null;
  if (!app) return null;
  const candidates: unknown[] = [
    app['appId'],
    app['app_id'],
    (app['app'] as Record<string, unknown> | undefined)?.['id'],
    app['app'],
    app['id'], // last — usually the composite form
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return bareAppId(c);
    if (typeof c === 'number' && Number.isFinite(c)) return String(c);
  }
  // Fallback: identity-match within projectAppsByAppID, whose keys are bare.
  if (appsByAppID && typeof appsByAppID === 'object') {
    for (const [k, v] of Object.entries(appsByAppID as Record<string, unknown>)) {
      if (v === app) return bareAppId(k);
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Serialisation
// ---------------------------------------------------------------------------

/** A formula cell's `v` is never authoritative; drop it. */
function thinCells(cells: Sheet['cells']): Sheet['cells'] {
  return Object.fromEntries(
    Object.entries(cells).map(([k, cell]) => {
      const { f, v, fmt, s: style } = cell;
      const out: Cell = {};
      if (f !== undefined) out.f = f;
      else if (v !== undefined) out.v = v;
      if (fmt !== undefined) out.fmt = fmt;
      if (style !== undefined) out.s = style;
      return [k, out];
    })
  );
}

/** The header row as text, by column — see `Sheet.headers`. */
export function headerRow(sheet: Sheet): (string | null)[] {
  const out: (string | null)[] = [];
  const width = Math.max(sheet.cols, 1);
  for (let c = 1; c <= width; c++) {
    const v = sheet.cells[`1,${c}`]?.v;
    out.push(v === undefined || v === null || v === '' ? null : String(v));
  }
  while (out.length > 0 && out[out.length - 1] === null) out.pop();
  return out;
}

/**
 * Save what cannot be rebuilt. Rebuild everything else.
 *
 * For a user sheet that is everything — formulas, formats, styles, charts;
 * nothing else knows them. For a SYSTEM sheet (Usages, Features, Blocks) it is
 * almost nothing: `ensureSystemSheets` writes those cells from the live model on
 * every load and every broadcast, so a saved copy of them can only be large and
 * occasionally wrong. Measured on the demo project — five features, four usages,
 * two blocks — the three system sheets were 66% of the payload; they scale with
 * the model, and the user's own work does not.
 *
 * What IS kept is how the person arranged them: the column order (as the header
 * text — see `Sheet.headers`), widths, hidden bands, freeze, filters, dropdowns
 * and the sort. That is O(columns), a couple of kB, and it is the only thing the
 * saved file is authoritative about.
 *
 * `entityBase` goes too, and `entityRows` with it: they are the project's
 * last-known value per cell and per row, and after a rebuild the sheet, the
 * data and the two of them are the same thing.
 */
function sheetView(s: Sheet): Sheet {
  if (!s.role) return { ...s, cells: thinCells(s.cells) };
  const { entityBase: _base, entityRows: _rows, ...rest } = s;
  return { ...rest, cells: {}, headers: headerRow(s) };
}

export function serialize(wb: Workbook, savedAt = Date.now()): PersistedWorkbook {
  const sheets = wb.sheets.map(sheetView);
  return {
    version: SCHEMA_VERSION,
    savedAt,
    // Always written, so a load can tell a workbook saved since `'areas'`
    // became the GI.* default from one saved before it (see `deserialize`).
    workbook: { ...wb, sheets, giSource: wb.giSource ?? 'areas' } as Workbook,
  };
}

export function savedAtOf(raw: unknown): number {
  if (!raw || typeof raw !== 'object') return 0;
  const at = (raw as { savedAt?: unknown }).savedAt;
  return typeof at === 'number' && Number.isFinite(at) ? at : 0;
}

/**
 * A sheet filter clause used to carry a single `value: string` rather than
 * `values: string[]`. Projects saved before that change still have the old
 * shape sitting in their project data — without this, loading one throws the
 * moment anything reads `.values` (Toolbar's filter dot, FilterBar itself).
 */
function migrateSheetFilter(sheet: Sheet): Sheet {
  if (!Array.isArray(sheet.sheetFilter)) return sheet;
  let changed = false;
  const sheetFilter = sheet.sheetFilter.map((c) => {
    if (Array.isArray((c as { values?: unknown }).values)) return c;
    changed = true;
    const old = c as unknown as { field: string; value?: string; op?: 'visible' };
    return { field: old.field, values: old.value ? [old.value] : [], op: old.op };
  });
  return changed ? { ...sheet, sheetFilter } : sheet;
}

export function deserialize(raw: unknown): Workbook | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Partial<PersistedWorkbook>;
  if (obj.version !== SCHEMA_VERSION) return null;
  const wb = obj.workbook;
  if (!wb || !Array.isArray(wb.sheets) || wb.sheets.length === 0) return null;
  return {
    sheets: wb.sheets.map(migrateSheetFilter),
    activeSheetId: wb.activeSheetId ?? wb.sheets[0]!.id,
    blocks: wb.blocks ?? {},
    charts: wb.charts ?? {},
    // Rebuilt field by field rather than spread, so every persisted key has to
    // be named here to survive. `fieldClasses` was not, and a measure class the
    // user corrected was written on every save and dropped on every load.
    ...(wb.fieldClasses ? { fieldClasses: wb.fieldClasses } : {}),
    ...(Array.isArray(wb.hiddenFeatures) ? { hiddenFeatures: wb.hiddenFeatures } : {}),
    ...(wb.units === 'metric' || wb.units === 'imperial' ? { units: wb.units } : {}),
    ...(wb.namedRanges && typeof wb.namedRanges === 'object' ? { namedRanges: wb.namedRanges } : {}),
    // Every workbook reads the Areas rows by default — including ones saved
    // before that was the default, which were briefly pinned to
    // bakedSections. Nothing in the UI sets giSource, so any stored value
    // came from that migration. A formula still reaches baked sections by
    // naming the source explicitly (see Help → Advanced).
    giSource: 'areas',
  };
}

/**
 * Content hash. `savedAt` is deliberately excluded by the caller (it passes 0),
 * because hashing the timestamp would make every serialisation look like a
 * change and the dedupe would never fire.
 */
export function hash(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return `${h}:${text.length}`;
}



/**
 * What the Save control should say, and whether it can be pressed.
 *
 * Pure, because the three-way "is there anything to save / is one in flight /
 * did the last one fail" is the part worth pinning down, and a button is a bad
 * place to keep a decision.
 */
export function saveAffordance(status: SaveStatus): { label: string; busy: boolean; dirty: boolean } {
  const busy = status.state === 'saving' || status.state === 'retrying';
  const failed = status.state === 'error';
  // A failure is dirty by definition: the work did not land, so there is still
  // something to send, and the control has to stay pressable to send it.
  const dirty = status.state === 'pending' || failed;
  return { label: busy ? 'Saving…' : failed ? 'Retry' : dirty ? 'Save' : 'Saved ✓', busy, dirty };
}

/** "12s ago" / "3m 04s ago" / never. What "saved" alone does not tell anyone. */
export function agoLabel(lastSavedAt: number | null, now = Date.now()): string {
  if (lastSavedAt == null) return 'never saved this session';
  const s = Math.max(0, Math.round((now - lastSavedAt) / 1000));
  return s < 60 ? `${s}s ago` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s ago`;
}
